import React from 'react';
import { Box, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { fmtMoney, plural } from '../../Documents/format';
import { BUCKET_COLORS } from '../../OnlineJournal/constants';
import { daysLeftLabel, fmtDay, fmtInt, fmtQty } from '../../OnlineJournal/format';
import { PharmacySummary, bucketKeyOf, fmtDistance } from '../remainsCalc';
import { BORDER, OPEN_COLORS } from '../remainsStyles';

interface Props {
  summary: PharmacySummary | null;
  loading: boolean;
  withSums: boolean;
  withContacts: boolean;
  /** Есть точка отсчёта - показываем ближайшую аптеку */
  withOrigin: boolean;
  onOpenPharmacy: (guid: string) => void;
}

interface TileProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  color?: string;
  tooltip?: string;
  onClick?: () => void;
}

/** Плитка показателя - как в «Электронном журнале» */
const Tile: React.FC<TileProps> = ({ label, value, hint, color, tooltip, onClick }) => {
  const body = (
    <Paper
      variant="outlined" onClick={onClick}
      sx={{
        px: 1.75, py: 1.25, borderRadius: 2, minWidth: 0, height: '100%', boxSizing: 'border-box', borderColor: BORDER, bgcolor: '#fff',
        cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s',
        '&:hover': onClick ? { borderColor: '#94a3b8' } : undefined
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }} noWrap>{label}</Typography>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: color || '#0f172a', lineHeight: 1.35, mt: 0.25 }} noWrap>{value}</Typography>
      {hint && <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.25 }} noWrap>{hint}</Typography>}
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip} arrow placement="top">{body}</Tooltip> : body;
};

const gridSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.25 };

/** Сводка по аптекам, прошедшим фильтры, и выбранным товарам */
const RemainsSummary: React.FC<Props> = ({ summary, loading, withSums, withContacts, withOrigin, onOpenPharmacy }) => {
  if (!summary) {
    return (
      <Box sx={gridSx}>
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rounded" height={86} sx={{ borderRadius: 2 }} />)}
      </Box>
    );
  }

  const s = summary;
  const unknown = s.pharmacies - s.open - s.closed;
  const nearestColor = s.nearest ? BUCKET_COLORS[bucketKeyOf(s.nearest)].color : undefined;
  const closest = s.closest;

  return (
    <Box sx={{ ...gridSx, opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
      <Tile
        label="Аптек с остатком" value={fmtInt(s.pharmacies)}
        hint={`открыто: ${fmtInt(s.open)} · закрыто: ${fmtInt(s.closed)}${unknown ? ` · без графика: ${fmtInt(unknown)}` : ''}`}
        tooltip="Аптеки зоны, где есть найденные (или выбранные) товары, с учётом фильтров списка"
      />
      <Tile
        label="Количество" value={`${fmtQty(s.units)} уп.`}
        hint={`${fmtInt(s.positions)} ${plural(s.positions, 'позиция', 'позиции', 'позиций')} в аптеках`}
      />
      {withSums && (
        <Tile label="Сумма по остаткам" value={fmtMoney(s.amount)} hint="по ценам аптек из 1С" />
      )}
      <Tile
        label="Ближайший срок" value={s.nearest ? fmtDay(s.nearest.date) : 'не указан'} color={nearestColor}
        hint={s.expiredQty
          ? <Box component="span" sx={{ color: BUCKET_COLORS.expired.color }}>просрочено в журнале: {fmtQty(s.expiredQty)} уп.</Box>
          : s.nearest ? daysLeftLabel(s.nearest.daysLeft) : 'нет записей в журнале сроков'}
        tooltip="По «Электронному журналу» сроков: ближайшая неистёкшая дата среди найденных товаров (серии не учитываются)"
      />
      {withOrigin && (
        <Tile
          label="Ближайшая аптека"
          value={closest ? fmtDistance(closest.distance) : '-'}
          color={closest && closest.pharmacy.open === true ? OPEN_COLORS.open.color : undefined}
          hint={closest ? closest.pharmacy.name : 'нет аптек с координатами'}
          tooltip={closest ? 'Открыть карточку аптеки' : undefined}
          onClick={closest ? () => onOpenPharmacy(closest.pharmacy.guid) : undefined}
        />
      )}
      <Tile
        label="Без графика работы" value={fmtInt(s.noHours)}
        color={s.noHours ? '#b45309' : undefined}
        hint={withContacts ? `без рабочего телефона: ${fmtInt(s.noPhone)}` : 'из аптек в списке'}
        tooltip="Режим работы и рабочий телефон берутся из профиля аптеки - заполняются в «Пользователях»"
      />
    </Box>
  );
};

export default React.memo(RemainsSummary);
